import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  Bell,
  CheckCircle,
  Trash2,
  Clock,
  X,
  ChevronDown,
  PawPrint,
} from 'lucide-react';
import {
  useNotifications,
  type Notification,
  type NotificationType,
} from '../context/NotificationsContext';
import { usePets } from '../context/PetsContext';
import { formatDistanceToNow } from '../utils/formatTime';
import { useTheme } from '../context/ThemeContext';
import { cn } from './ui/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const NOTIFICATION_ICONS: Record<NotificationType, string> = {
  proposal_received: '📬',
  proposal_approved: '✅',
  proposal_rejected: '❌',
  edit_requested: '✏️',
  appointment_upcoming: '📅',
  appointment_confirmed: '✔️',
  appointment_reminder: '📅',
  document_shared: '📄',
  access_granted: '🔓',
  access_revoked: '🔒',
  vaccine_due: '⚠️',
  reminder: '⏰',
  reward_earned: '🪙',
  social: '👥',
  pet_parks: '🗺️',
  record_added: '📋',
  system: '🔔',
};

interface NotificationSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigateTo?: (notification: Notification) => void;
}

export function NotificationSidePanel({
  open,
  onOpenChange,
  onNavigateTo,
}: NotificationSidePanelProps) {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    deleteNotification,
  } = useNotifications();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { pets } = usePets();
  const panelRef = useRef<HTMLDivElement>(null);
  const [selectedPetIds, setSelectedPetIds] = useState<string[]>([]);

  const togglePetSelection = (petId: string) => {
    setSelectedPetIds((prev) =>
      prev.includes(petId)
        ? prev.filter((id) => id !== petId)
        : [...prev, petId]
    );
  };
  const isAllPetsSelected = selectedPetIds.length === 0;
  const petFilterLabel = isAllPetsSelected
    ? t('notificationSidePanel.allPets')
    : selectedPetIds.length === 1
      ? (pets.find((p) => p.id === selectedPetIds[0])?.name ?? '1 pet')
      : `${selectedPetIds.length} pets`;

  const handleNotificationClick = (notif: Notification) => {
    onNavigateTo?.(notif);
    onOpenChange(false);
  };

  const handleMarkAsRead = (e: React.MouseEvent, notifId: string) => {
    e.stopPropagation();
    markAsRead(notifId);
  };

  const handleDelete = (e: React.MouseEvent, notifId: string) => {
    e.stopPropagation();
    deleteNotification(notifId);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const handleClearAll = () => {
    clearAll();
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  // Latest first, then filter by selected pets
  const sortedNotifications = [...notifications]
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
    .filter((n) => {
      if (selectedPetIds.length === 0) return true;
      if (!n.petId) return true;
      return selectedPetIds.includes(n.petId);
    });

  if (!open) return null;

  const overlay = (
    <div
      role="presentation"
      className="fixed inset-0 z-[9998]"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onClick={() => onOpenChange(false)}
      onKeyDown={(e) => e.key === 'Escape' && onOpenChange(false)}
    />
  );

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('notificationSidePanel.title')}
      className="rounded-2xl border"
      style={{
        position: 'fixed',
        right: '24px',
        top: '24px',
        bottom: '24px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        width: 'min(380px, calc(100vw - 48px))',
        maxWidth: '100%',
        backgroundColor: colors.surface ?? 'var(--background)',
        borderColor: colors.border,
        boxShadow:
          '0 25px 50px -12px rgba(0,0,0,0.2), 0 12px 24px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06), 0 0 40px -10px rgba(99,102,241,0.15)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Scrollable list */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        <div className="p-3 pb-6">
          {sortedNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Bell
                className="w-12 h-12 mb-3 opacity-30"
                style={{ color: colors.textSecondary }}
              />
              <p className="text-sm font-medium" style={{ color: colors.text }}>
                {notifications.length > 0 && selectedPetIds.length > 0
                  ? t('notificationSidePanel.noNotificationsForSelectedPets')
                  : t('notificationSidePanel.noNotificationsYet')}
              </p>
              <p
                className="text-xs mt-1"
                style={{ color: colors.textSecondary }}
              >
                {notifications.length > 0 && selectedPetIds.length > 0
                  ? t('notificationSidePanel.trySelectingPets')
                  : t('notificationSidePanel.updatesFromModules')}
              </p>
            </div>
          ) : (
            <ul className="space-y-3" role="list">
              {sortedNotifications.map((notif) => (
                <li key={notif.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNotificationClick(notif)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationClick(notif);
                      }
                    }}
                    className={cn(
                      'rounded-xl p-3 border transition-colors cursor-pointer flex gap-3',
                      !notif.read && 'border-l-[3px]'
                    )}
                    style={{
                      backgroundColor: notif.read
                        ? colors.surface
                        : colors.surfaceHover,
                      borderColor: notif.read
                        ? (colors.border ?? 'rgba(0,0,0,0.08)')
                        : colors.primary + '40',
                      ...(!notif.read && {
                        borderLeftColor: colors.primary + '99',
                      }),
                    }}
                  >
                    <span className="text-xl flex-shrink-0" aria-hidden>
                      {notif.icon ?? NOTIFICATION_ICONS[notif.type]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-xs font-medium uppercase tracking-wide"
                          style={{ color: colors.primary }}
                        >
                          {notif.type === 'pet_parks'
                            ? t('navigation.petParks')
                            : t(`notificationSidePanel.types.${notif.type}`, {
                                defaultValue: notif.type,
                              })}
                        </span>
                        {notif.source && (
                          <span
                            className="text-xs"
                            style={{ color: colors.textSecondary }}
                          >
                            · {notif.source}
                          </span>
                        )}
                      </div>
                      <p
                        className="text-sm font-medium mt-0.5 line-clamp-2"
                        style={{ color: colors.text }}
                      >
                        {notif.title}
                      </p>
                      <p
                        className="text-xs mt-0.5 line-clamp-2"
                        style={{ color: colors.textSecondary }}
                      >
                        {notif.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {notif.petName && (
                          <span
                            className="text-xs"
                            style={{ color: colors.textSecondary }}
                          >
                            {notif.petName}
                          </span>
                        )}
                        <span
                          className="flex items-center gap-1 text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(notif.timestamp))}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {!notif.read && (
                        <button
                          type="button"
                          onClick={(e) => handleMarkAsRead(e, notif.id)}
                          className="p-1.5 rounded-md transition-opacity hover:opacity-80"
                          style={{ color: colors.primary }}
                          title="Mark as read"
                          aria-label="Mark as read"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => handleDelete(e, notif.id)}
                        className="p-1.5 rounded-md transition-opacity hover:opacity-80"
                        style={{ color: colors.textSecondary }}
                        title="Remove"
                        aria-label="Remove notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Footer bar: title, actions, filters, close - at bottom so not hidden by dashboard header */}
      <div
        className="border-t px-3 py-2"
        style={{ flexShrink: 0, borderColor: colors.border }}
      >
        <div className="flex items-center gap-2 flex-nowrap min-w-0">
          <Bell
            className="w-4 h-4 shrink-0"
            style={{ color: colors.primary }}
          />
          <span
            className="text-sm font-semibold truncate shrink-0"
            style={{ color: colors.text }}
          >
            {t('notificationSidePanel.title')}
          </span>
          {unreadCount > 0 && (
            <span
              className="flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold text-white shrink-0"
              style={{ backgroundColor: colors.primary }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          <div className="flex-1 min-w-3" aria-hidden />
          {notifications.length > 0 && (
            <>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium shrink-0 transition-colors hover:opacity-90"
                  style={{
                    backgroundColor: colors.primary + '18',
                    color: colors.primary,
                  }}
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t('notificationSidePanel.markRead')}</span>
                </button>
              )}
              <button
                type="button"
                onClick={handleClearAll}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs shrink-0 transition-colors hover:opacity-90"
                style={{
                  backgroundColor: colors.surfaceHover,
                  color: colors.textSecondary,
                }}
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t('notificationSidePanel.clear')}</span>
              </button>
              {pets.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs shrink-0 transition-colors hover:opacity-90"
                      style={{
                        backgroundColor: colors.surfaceHover,
                        color: colors.textSecondary,
                      }}
                    >
                      <PawPrint className="w-3.5 h-3.5" />
                      <span className="max-w-[5rem] truncate">
                        {petFilterLabel}
                      </span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>{t('notificationSidePanel.filterByPet')}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={isAllPetsSelected}
                      onCheckedChange={() => setSelectedPetIds([])}
                    >
                      {t('notificationSidePanel.allPets')}
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuSeparator />
                    {pets.map((pet) => (
                      <DropdownMenuCheckboxItem
                        key={pet.id}
                        checked={selectedPetIds.includes(pet.id)}
                        onCheckedChange={() => togglePetSelection(pet.id)}
                      >
                        {pet.name}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
          <div className="w-2 shrink-0" aria-hidden />
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors hover:opacity-80"
            style={{
              backgroundColor: colors.surfaceHover,
              color: colors.textSecondary,
            }}
            aria-label={t('notificationSidePanel.closeAria')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const portalContainer =
    document.getElementById('portal-root') ?? document.body;

  return createPortal(
    <>
      {overlay}
      {panel}
    </>,
    portalContainer
  );
}
